/**
 * PreviewPageShell — chrome shared by all program-preview routes:
 *   /programs/:id/preview              (welcome)
 *   /programs/:id/preview/days         (days overview)
 *   /programs/:id/preview/days/:dayId  (courses for one day)
 *
 * Each page passes its own body content + the up-link target. The shell
 * owns the top back link, the page container, the Toaster, and the bottom
 * PreviewActions ("Go back to Editing" / "Publish now!"). Centralising the
 * publish flow here means each subpage just composes its own visuals.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { useUpdateProgramStatus } from '../../../services/programs';
import PreviewActions from './PreviewActions';

interface PreviewPageShellProps {
  programId: string;
  /** Route the top "← Back" link goes to. */
  upHref: string;
  /** Label next to the top "←" arrow ("Back to Program", "Back to Welcome", …). */
  upLabel: string;
  /** Pass `true` to disable the Publish button when the program is already active. */
  programIsActive: boolean;
  children: React.ReactNode;
}

function ArrowLeftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <line x1="19" y1="12" x2="5" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <polyline points="12 19 5 12 12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function PreviewPageShell({ programId, upHref, upLabel, programIsActive, children }: PreviewPageShellProps) {
  const navigate = useNavigate();
  const updateStatus = useUpdateProgramStatus();

  const handlePublish = async () => {
    try {
      await updateStatus.mutateAsync({ programId, status: 'active' });
      toast.success('Program published — visible to learners.');
      navigate(`/programs/${programId}`);
    } catch {
      toast.error('Could not activate the program. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-background-primary">
      <Toaster position="top-right" />

      <div className="max-w-[1080px] mx-auto px-4 sm:px-6 pt-6">
        <button
          type="button"
          onClick={() => navigate(upHref)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-text-secondary hover:text-text-primary active:scale-[0.97] transition-[color,transform] duration-150"
        >
          <ArrowLeftIcon /> {upLabel}
        </button>
      </div>

      <div className="max-w-[1080px] mx-auto px-4 sm:px-6 pt-5 pb-16 flex flex-col gap-6 sm:gap-8">
        {children}

        <PreviewActions
          back={{
            label: 'Go back to Editing',
            onClick: () => navigate(`/programs/${programId}/edit`),
          }}
          primary={{
            label: programIsActive ? 'Already published' : 'Publish now!',
            onClick: handlePublish,
            disabled: programIsActive,
            busy: updateStatus.isPending,
          }}
        />
      </div>
    </div>
  );
}
